import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { filter, withLatestFrom, switchMap, catchError, map, tap, first } from 'rxjs/operators';
import { CartActions, TypeMap as CartTypeMap } from '@wearejh/m2-pwa-cart-gql';

import { fireGTMEvent } from 'src/features/analytics/hooks/useGtm';
import { Product } from 'src/features/product-store/types';

import {
    HubspotAddedProductToCartGtmEvent,
    HubspotAnalyticEvents,
    HubspotEvents,
    HubspotProduct,
    HubspotTagManagerEvents,
} from '../types';
import { isSubscribedUser } from '../is-subscribed-user';

/**
 * Tracks "add to cart" events and sends them to Hubspot via GTM.
 * Uses Redux product store for consistent product data.
 */
export function hubspotAddedProductToCart(action$: Observable<CartActions>, state$: Observable<any>): Observable<any> {
    return action$.pipe(
        // Start with AddSuccess event because that has the payload
        // that we need to send to Hubspot.
        ofType<CartActions, CartTypeMap['Cart.Add']>('Cart.Add'),

        // Store event payload
        switchMap((addAction) => {
            // Store the original product data being added
            const addedProductData = addAction?.payload ?? {};

            // Listen for the success action to ensure the product was added
            // and then wait for the cart to be refreshed
            return action$.pipe(
                ofType<CartActions>('Cart.AddSuccess'),

                switchMap(() => action$.pipe(ofType<CartActions>('Cart.FetchSuccess'), first())),

                withLatestFrom(state$),

                filter(([, state]) => isSubscribedUser(state)),

                // Pass both the original product data and the updated state
                map(([, state]) => ({
                    addedProductData,
                    state,
                })),
            );
        }),

        switchMap(({ addedProductData, state }) => {
            const baseUrl = window?.location?.origin ?? '';
            const cart_id = state.cart?.cartId ?? undefined;
            const cart_url = baseUrl + '/basket';
            const items_count = state.cart?.items?.length;
            const items_qty = state.cart?.items_qty ?? 0;
            const total_value = state.cart?.totals?.grand_total ?? 0;

            const findProductBySku = (sku) => {
                const productsById: Record<string, Product> = state.products?.byIds ?? {};

                return Object.values(productsById).find((product: Product) => product?.sku === sku);
            };

            const cartItems: HubspotProduct[] = addedProductData?.items?.map((cartItem) => {
                const sku = cartItem?.sku;

                const product = findProductBySku(sku);
                const item_id = product?.id;

                const sale_price = product?.price?.special_price ?? undefined;
                const maxPrice = product?.price?.maximum_price ?? (cartItem as any)?.prices?.price ?? undefined;

                let price = maxPrice;
                let was_price;

                if (sale_price) {
                    price = sale_price;
                    was_price = maxPrice;
                }

                let product_url = product?.pathname ?? undefined;

                if (product_url && !product_url?.startsWith('http')) {
                    product_url = baseUrl + '/' + product_url;
                }

                let product_image_url = product?.small_image ?? undefined;

                if (product_image_url && !product_image_url.startsWith('http')) {
                    product_image_url = baseUrl + product_image_url;
                }

                const labels =
                    product?.labels
                        ?.map((label) => label.name)
                        .filter((label): label is string => label !== null && label !== undefined) ?? [];

                return {
                    currency: state.cart?.raw?.prices?.grand_total?.currency ?? 'EUR',
                    item_id,
                    labels,
                    name: product?.name,
                    price,
                    product_image_url,
                    product_url,
                    quantity: cartItem?.quantity,
                    sale_price,
                    save_price: sale_price,
                    sku,
                    was_price,
                };
            });

            // Format data according to Hubspot requirements
            const gtmEvent: HubspotAddedProductToCartGtmEvent = {
                cart_id,
                cart: {
                    cart_id,
                    cart_url,
                    delivery_type: state.cart?.raw?.woodies?.basket_type,
                    items_count,
                    items_qty,
                    total_value,
                },
                customer_email: state.user?.data?.email,
                event_name: HubspotAnalyticEvents.ADDED_PRODUCT_TO_CART,
                event: HubspotTagManagerEvents.ADDED_PRODUCT_TO_CART,
                firstname: state.user?.data?.firstname,
                is_guest: state.user?.session !== 'IsSignedIn',
                items: cartItems,
                lastname: state.user?.data?.lastname,
                session_id: cart_id,
                timestamp: new Date().toISOString(),
            };

            return of(gtmEvent);
        }),

        // Trigger the GTM event
        tap((gtmEvent) => fireGTMEvent(gtmEvent)),

        // Send the event to Redux
        map((gtmEvent) => ({
            type: HubspotEvents.ADDED_PRODUCT_TO_CART,
            data: gtmEvent,
        })),

        catchError((error) => {
            console.error('Error in hubspotAddedProductToCart epic:', error);
            return of({
                type: HubspotEvents.ADDED_PRODUCT_TO_CART_ERROR,
                payload: error,
            });
        }),
    );
}
